import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config/api';
import { 
  ArrowRight, ArrowLeft, Check, Wallet, TrendingUp, TrendingDown,
  Home, Car, Wifi, Smartphone, ShoppingCart, Zap, Droplets, 
  Trash2, Plus, Sparkles, Building2, Lightbulb, Heart, PiggyBank,
  Shield, CircleDollarSign, Target, CreditCard, Coins, User,
  Calendar, PartyPopper, Gift
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { 
  FREQUENCIES,
  getFrequencyByValue, 
  getFrequencyLabel,
  REVENUE_EXPENSE_FREQUENCIES
} from '../constants/frequencies';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// =============================================================================
// CONSTANTS
// =============================================================================
// Utilisation du système de fréquences harmonisé
// Les fréquences pour l'onboarding sont limitées aux plus courantes pour simplifier l'UX
const ONBOARDING_FREQUENCIES = FREQUENCIES.filter(f => 
  ['mensuel', 'hebdomadaire', 'quinzaine', 'trimestriel', 'annuel'].includes(f.value)
);

const INCOME_SUGGESTIONS = [
  { name: 'Salaire', icon: Wallet, category: 'Salaire', defaultDay: 25, defaultAmount: '' },
  { name: 'Prime', icon: Sparkles, category: 'Prime', defaultDay: 25, defaultAmount: '' },
  { name: 'Allocations (CAF)', icon: Heart, category: 'Allocations', defaultDay: 5, defaultAmount: '' },
  { name: 'Pension', icon: PiggyBank, category: 'Pension', defaultDay: 1, defaultAmount: '' },
  { name: 'Revenus locatifs', icon: Home, category: 'Revenus locatifs', defaultDay: 1, defaultAmount: '' },
  { name: 'Freelance', icon: Zap, category: 'Freelance', defaultDay: 15, defaultAmount: '' },
];

const EXPENSE_SUGGESTIONS = [
  { name: 'Loyer', icon: Home, category: 'Logement', defaultDay: 5, defaultAmount: '' },
  { name: 'Électricité', icon: Zap, category: 'Énergie', defaultDay: 10, defaultAmount: '' },
  { name: 'Eau', icon: Droplets, category: 'Énergie', defaultDay: 10, defaultAmount: '' },
  { name: 'Internet', icon: Wifi, category: 'Abonnements', defaultDay: 15, defaultAmount: '' },
  { name: 'Téléphone', icon: Smartphone, category: 'Abonnements', defaultDay: 15, defaultAmount: '' },
  { name: 'Assurance auto', icon: Car, category: 'Assurance', defaultDay: 1, defaultAmount: '' },
  { name: 'Assurance habitation', icon: Shield, category: 'Assurance', defaultDay: 1, defaultAmount: '' },
  { name: 'Crédit auto', icon: CreditCard, category: 'Crédit', defaultDay: 5, defaultAmount: '' },
  { name: 'Crédit immobilier', icon: Building2, category: 'Crédit', defaultDay: 5, defaultAmount: '' },
  { name: 'Mutuelle santé', icon: Heart, category: 'Santé', defaultDay: 1, defaultAmount: '' },
];

// =============================================================================
// MAIN COMPONENT - Unified Onboarding Wizard
// =============================================================================
const FinancialSetupWizard = ({ open, onComplete, userName, isFirstTimeUser = true }) => {
  // State
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Profile data (NEW - merged from ProfileSetup)
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    birthdate: '',
    consent_birthday_message: true
  });

  // Form data - Financial
  const [initialBalance, setInitialBalance] = useState('');
  const [initialBalanceDate, setInitialBalanceDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  
  // Réserve de sécurité - modifiable par l'utilisateur (système hybride)
  const [customEmergencyReserve, setCustomEmergencyReserve] = useState(null);

  // New entry forms
  const [newIncome, setNewIncome] = useState({
    name: '', amount: '', day: 1, category: 'Salaire', frequency: 'mensuel'
  });
  const [newExpense, setNewExpense] = useState({
    name: '', amount: '', day: 1, category: 'Logement', frequency: 'mensuel'
  });

  // Initialize profile from userName if available
  useEffect(() => {
    if (userName && isFirstTimeUser) {
      setProfileData(prev => ({
        ...prev,
        first_name: prev.first_name || userName.split(' ')[0] || '',
        last_name: prev.last_name || userName.split(' ').slice(1).join(' ') || ''
      }));
    }
  }, [userName, isFirstTimeUser]);

  // Unified steps configuration - Profile + Financial in one flow
  const steps = isFirstTimeUser ? [
    { title: 'Bienvenue', icon: Sparkles, description: 'Découvre ton espace budget' },
    { title: 'Ton profil', icon: User, description: 'Prénom, nom, anniversaire' },
    { title: 'Solde initial', icon: CircleDollarSign, description: 'Ton point de départ' },
    { title: 'Revenus', icon: TrendingUp, description: 'Tes sources de revenus' },
    { title: 'Dépenses', icon: TrendingDown, description: 'Tes charges fixes' },
    { title: 'Récapitulatif', icon: Check, description: 'Validation finale' },
  ] : [
    { title: 'Bienvenue', icon: Sparkles, description: 'Configuration de ta base financière' },
    { title: 'Solde initial', icon: CircleDollarSign, description: 'Ton point de départ' },
    { title: 'Revenus', icon: TrendingUp, description: 'Tes sources de revenus' },
    { title: 'Dépenses', icon: TrendingDown, description: 'Tes charges fixes' },
    { title: 'Récapitulatif', icon: Check, description: 'Validation finale' },
  ];

  // Calculate totals
  const totalIncome = incomes.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  const monthlyBalance = totalIncome - totalExpenses;
  
  // Réserve de sécurité - calcul par défaut (10% des revenus, min 20€, max 200€)
  const defaultEmergencyReserve = monthlyBalance > 0 
    ? Math.min(Math.max(totalIncome * 0.10, 20), 200)
    : 0;
  
  // Utiliser la valeur personnalisée si définie, sinon le calcul par défaut
  const emergencyReserve = customEmergencyReserve !== null 
    ? parseFloat(customEmergencyReserve) 
    : defaultEmergencyReserve;
  
  const availableBalance = monthlyBalance - emergencyReserve;
  
  // Calculate 6-month projection showing capital + flux evolution
  const initialBalanceNum = parseFloat(initialBalance) || 0;
  const projection6Months = Array.from({ length: 6 }, (_, i) => {
    const projectedBalance = initialBalanceNum + (monthlyBalance * (i + 1));
    const monthDate = new Date(initialBalanceDate);
    monthDate.setMonth(monthDate.getMonth() + i + 1);
    return {
      month: monthDate.toLocaleDateString('fr-FR', { month: 'short' }),
      balance: projectedBalance
    };
  });

  // Get the step index mapping based on whether profile is included
  const getStepIndex = (stepName) => {
    if (isFirstTimeUser) {
      const mapping = {
        welcome: 0,
        profile: 1,
        balance: 2,
        incomes: 3,
        expenses: 4,
        summary: 5
      };
      return mapping[stepName];
    } else {
      const mapping = {
        welcome: 0,
        balance: 1,
        incomes: 2,
        expenses: 3,
        summary: 4
      };
      return mapping[stepName];
    }
  };

  // Check step validity for navigation
  const canProceed = () => {
    if (isFirstTimeUser) {
      if (step === 1) {
        // Profile step - require first_name, last_name, birthdate
        return profileData.first_name.trim() && profileData.last_name.trim() && profileData.birthdate;
      }
      if (step === 2) return initialBalance; // Balance step
    } else {
      if (step === 1) return initialBalance; // Balance step
    }
    return true;
  };

  // Handle profile field change
  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  // Quick add income from suggestion
  const quickAddIncome = (suggestion) => {
    setNewIncome({
      name: suggestion.name,
      amount: suggestion.defaultAmount || '',
      day: suggestion.defaultDay,
      category: suggestion.category,
      frequency: 'mensuel'
    });
  };

  // Quick add expense from suggestion
  const quickAddExpense = (suggestion) => {
    setNewExpense({
      name: suggestion.name,
      amount: suggestion.defaultAmount || '',
      day: suggestion.defaultDay,
      category: suggestion.category,
      frequency: 'mensuel'
    });
  };

  // Add income
  const addIncome = () => {
    if (!newIncome.name || !newIncome.amount || parseFloat(newIncome.amount) <= 0) {
      toast.error('Remplis le nom et un montant valide');
      return;
    }
    setIncomes(prev => [...prev, { 
      ...newIncome, 
      id: `new-${Date.now()}`,
      amount: parseFloat(newIncome.amount),
      day: parseInt(newIncome.day) || 1
    }]);
    setNewIncome({ name: '', amount: '', day: 1, category: 'Salaire', frequency: 'mensuel' });
    toast.success(`${newIncome.name} ajouté !`);
  };

  // Add expense
  const addExpense = () => {
    if (!newExpense.name || !newExpense.amount || parseFloat(newExpense.amount) <= 0) {
      toast.error('Remplis le nom et un montant valide');
      return;
    }
    setExpenses(prev => [...prev, { 
      ...newExpense, 
      id: `new-${Date.now()}`,
      amount: parseFloat(newExpense.amount),
      day: parseInt(newExpense.day) || 1
    }]);
    setNewExpense({ name: '', amount: '', day: 1, category: 'Logement', frequency: 'mensuel' });
    toast.success(`${newExpense.name} ajouté !`);
  };

  // Remove income
  const removeIncome = (id) => {
    setIncomes(prev => prev.filter(i => i.id !== id));
    toast.success('Revenu supprimé');
  };

  // Remove expense
  const removeExpense = (id) => {
    setExpenses(prev => prev.filter(e => e.id !== id));
    toast.success('Dépense supprimée');
  };

  // Submit - Save profile + Create calendar + foundation + incomes/expenses
  const handleSubmit = async () => {
    setLoading(true);
    try {
      // 1. Save profile first (if first time user)
      if (isFirstTimeUser) {
        await axios.put(`${API}/user/profile`, profileData, { withCredentials: true });
      }

      // 2. Create calendar automatically with default name
      const calendarResponse = await axios.post(
        `${API}/calendars`,
        {
          name: 'Mon Budget Personnel',
          type: 'personal',
          initial_balance: parseFloat(initialBalance) || 0,
          initial_balance_date: initialBalanceDate,
          currency: '€'
        },
        { withCredentials: true }
      );
      
      const calendarId = calendarResponse.data.id;
      
      // 3. Setup foundation with incomes and expenses
      const payload = {
        initial_balance: parseFloat(initialBalance) || 0,
        initial_balance_date: initialBalanceDate,
        emergency_reserve_percentage: 0.10,
        incomes: incomes.map(i => {
          // Récupérer les infos de fréquence harmonisées
          const freqInfo = getFrequencyByValue(i.frequency);
          return {
            name: i.name,
            amount: parseFloat(i.amount),
            category: i.category,
            // Système de fréquence harmonisé
            frequency_type: i.frequency,
            frequency_value: freqInfo?.unitValue || 1,
            frequency_unit: freqInfo?.unit || 'mois',
            day_of_month: parseInt(i.day) || 1,
            start_date: new Date().toISOString().split('T')[0]
          };
        }),
        expenses: expenses.map(e => {
          // Récupérer les infos de fréquence harmonisées
          const freqInfo = getFrequencyByValue(e.frequency);
          return {
            name: e.name,
            amount: parseFloat(e.amount),
            category: e.category,
            // Système de fréquence harmonisé
            frequency_type: e.frequency,
            frequency_value: freqInfo?.unitValue || 1,
            frequency_unit: freqInfo?.unit || 'mois',
            day_of_month: parseInt(e.day) || 1,
            start_date: new Date().toISOString().split('T')[0],
            is_essential: true
          };
        })
      };

      await axios.post(
        `${API}/foundation/${calendarId}/setup`,
        payload,
        { withCredentials: true }
      );

      // 4. Generate recurring operations for current month
      const today = new Date();
      await axios.post(
        `${API}/generate-recurring?calendar_id=${calendarId}&year=${today.getFullYear()}&month=${today.getMonth() + 1}`,
        {},
        { withCredentials: true }
      );

      // 5. Mark onboarding as seen on server (if first time user)
      if (isFirstTimeUser) {
        await axios.post(`${API}/user/mark-onboarding-seen`, {}, { withCredentials: true });
      }

      setShowSuccess(true);
      toast.success('Ta base financière est prête !');

      // Callback after delay with firstName for personalization
      setTimeout(() => {
        if (onComplete) onComplete(calendarId, isFirstTimeUser ? profileData.first_name : null);
      }, 2500);

    } catch (error) {
      console.error('Error setting up foundation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // Success state - Elegant celebration animation with smooth transition
  if (showSuccess) {
    return (
      <motion.div 
        className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Background with smooth gradient fade */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-emerald-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
        
        {/* Subtle floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{
                background: i % 2 === 0 ? 'rgba(20, 184, 166, 0.4)' : 'rgba(16, 185, 129, 0.4)',
                left: `${15 + (i * 10)}%`,
                top: `${30 + (i % 3) * 15}%`,
              }}
              initial={{ opacity: 0, y: 50, scale: 0 }}
              animate={{ 
                opacity: [0, 0.5, 0],
                y: [50, -30],
                scale: [0, 1, 0.3]
              }}
              transition={{ 
                duration: 1.8,
                delay: 0.2 + (0.1 * i),
                ease: 'easeOut'
              }}
            />
          ))}
        </div>

        {/* Main content container with entry animation */}
        <motion.div 
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center justify-center text-center px-8 relative z-10"
        >
          {/* Checkmark icon with spring animation */}
          <motion.div
            initial={{ scale: 0, rotate: -90 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: 'spring', 
              stiffness: 150, 
              damping: 15,
              delay: 0.2 
            }}
            className="relative mb-6"
          >
            {/* Soft glow behind icon */}
            <motion.div
              className="absolute -inset-4 bg-gradient-to-br from-teal-400/30 to-emerald-500/30 rounded-full blur-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            />
            <div className="relative w-24 h-24 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center shadow-xl shadow-teal-200/50">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
              >
                <Check className="h-12 w-12 text-white" strokeWidth={3} />
              </motion.div>
            </div>
          </motion.div>

          {/* Text content with smooth stagger */}
          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5, ease: 'easeOut' }}
            className="text-2xl font-bold text-gray-900 mb-2"
          >
            {isFirstTimeUser && profileData.first_name 
              ? `Bravo ${profileData.first_name} ! 🎉` 
              : 'Bravo ! 🎉'
            }
          </motion.h2>

          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.5, ease: 'easeOut' }}
            className="text-lg text-teal-600 font-medium mb-1"
          >
            Ta base financière est prête
          </motion.p>

          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5, ease: 'easeOut' }}
            className="text-muted-foreground text-sm"
          >
            Ton calendrier est maintenant actif. À toi de jouer !
          </motion.p>

          {/* Elegant progress bar for transition */}
          <motion.div
            className="mt-8 h-1 bg-gray-200 rounded-full overflow-hidden w-32"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <motion.div
              className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ 
                delay: 1.1, 
                duration: 1.4, 
                ease: [0.22, 1, 0.36, 1]
              }}
            />
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }

  // Render step content
  const renderStep = () => {
    // Determine the actual step content based on isFirstTimeUser
    const welcomeIndex = getStepIndex('welcome');
    const profileIndex = getStepIndex('profile');
    const balanceIndex = getStepIndex('balance');
    const incomesIndex = getStepIndex('incomes');
    const expensesIndex = getStepIndex('expenses');
    const summaryIndex = getStepIndex('summary');

    // Welcome step
    if (step === welcomeIndex) {
      return (
        <div className="space-y-6 text-center">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring' }}
            className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-xl"
          >
            <Building2 className="h-10 w-10 text-white" />
          </motion.div>
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Bienvenue sur Leu Budget du Génie ! 🌴
            </h2>
            <p className="text-muted-foreground">
              {isFirstTimeUser 
                ? "En quelques étapes, configure ton profil et ta base financière pour que ton calendrier soit immédiatement opérationnel."
                : "En quelques étapes, définis tes revenus et dépenses pour que ton calendrier soit immédiatement opérationnel."
              }
            </p>
          </div>
          
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 border border-amber-200 text-left">
            <div className="flex gap-3">
              <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Ce que nous allons configurer :</strong>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  {isFirstTimeUser && <li>Tes informations personnelles</li>}
                  <li>Ton solde actuel (point de départ)</li>
                  <li>Tes revenus récurrents</li>
                  <li>Tes charges fixes</li>
                  <li>→ Le calendrier se remplit automatiquement !</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Profile step (only for first time users)
    if (isFirstTimeUser && step === profileIndex) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-2">
            <User className="h-12 w-12 text-teal-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-foreground">Ton profil</h2>
            <p className="text-sm text-muted-foreground">Pour personnaliser ton expérience</p>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Prénom *</Label>
                <Input
                  value={profileData.first_name}
                  onChange={(e) => handleProfileChange('first_name', e.target.value)}
                  placeholder="Ex : Marie"
                  className="h-12 mt-1"
                  data-testid="profile-first-name"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Nom *</Label>
                <Input
                  value={profileData.last_name}
                  onChange={(e) => handleProfileChange('last_name', e.target.value)}
                  placeholder="Ex : Dupont"
                  className="h-12 mt-1"
                  data-testid="profile-last-name"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Date de naissance *</Label>
              <Input
                type="date"
                value={profileData.birthdate}
                onChange={(e) => handleProfileChange('birthdate', e.target.value)}
                className="h-12 mt-1"
                max={new Date().toISOString().split('T')[0]}
                data-testid="profile-birthdate"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Pour te souhaiter un joyeux anniversaire le jour J ! 🎂
              </p>
            </div>

            <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent_birthday"
                  checked={profileData.consent_birthday_message}
                  onCheckedChange={(checked) => handleProfileChange('consent_birthday_message', checked)}
                  data-testid="consent-birthday-message"
                />
                <div className="flex-1">
                  <Label htmlFor="consent_birthday" className="text-sm font-medium text-teal-800 cursor-pointer">
                    Recevoir un message d'anniversaire
                  </Label>
                  <p className="text-xs text-teal-700 mt-1">
                    Le jour J, tu verras un message personnalisé 🎉
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-xs text-slate-600 text-center">
                🔒 Ces informations restent privées et ne seront jamais partagées.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Balance step
    if (step === balanceIndex) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-4">
            <CircleDollarSign className="h-12 w-12 text-blue-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-foreground">Ton solde actuel</h2>
            <p className="text-sm text-muted-foreground">Le point de départ de ton budget</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Solde actuel de ton compte (€)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Ex: 1500.00"
                value={initialBalance}
                onChange={(e) => setInitialBalance(e.target.value)}
                className="h-14 text-xl text-center font-semibold"
                data-testid="initial-balance-input"
              />
              <p className="text-xs text-muted-foreground mt-1 text-center">
                Indique le solde réel de ton compte aujourd'hui
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium">Date du solde</Label>
              <Input
                type="date"
                value={initialBalanceDate}
                onChange={(e) => setInitialBalanceDate(e.target.value)}
                className="h-12"
                data-testid="initial-balance-date-input"
              />
            </div>

            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <p className="text-sm text-blue-800">
                💡 Ce solde sera ton point de départ unique. Toutes les projections futures partiront de ce montant.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Incomes step
    if (step === incomesIndex) {
      return (
        <div className="space-y-4">
          <div className="text-center mb-2">
            <TrendingUp className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
            <h2 className="text-xl font-bold text-foreground">Tes revenus 💰</h2>
            <p className="text-sm text-muted-foreground">On commence par le positif !</p>
          </div>

          {/* Suggestions */}
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-2">Suggestions rapides :</p>
            <div className="flex flex-wrap gap-2">
              {INCOME_SUGGESTIONS.map((sug, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => quickAddIncome(sug)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all flex items-center gap-1 ${
                    newIncome.name === sug.name 
                      ? 'bg-emerald-200 text-emerald-800 border-emerald-400' 
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                  }`}
                >
                  <sug.icon className="h-3 w-3" />
                  {sug.name}
                </button>
              ))}
            </div>
          </div>

          {/* List of added incomes */}
          {incomes.length > 0 && (
            <div className="bg-emerald-50/50 rounded-xl p-3 border border-emerald-200 mb-4">
              <p className="text-xs font-medium text-emerald-800 mb-2">
                ✅ Revenus ajoutés ({incomes.length})
              </p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {incomes.map((income) => (
                  <div key={income.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-emerald-200">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm truncate block">{income.name}</span>
                      <span className="text-xs text-muted-foreground">
                        le {income.day} • {getFrequencyLabel(income.frequency, 'mois', 1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className="font-bold text-emerald-600 whitespace-nowrap">+{parseFloat(income.amount).toFixed(0)}€</span>
                      <button 
                        onClick={() => removeIncome(income.id)} 
                        className="text-gray-400 hover:text-red-500 p-1"
                        data-testid={`remove-income-${income.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-emerald-200">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Total mensuel :</span>
                  <span className="font-bold text-emerald-600">+{totalIncome.toFixed(0)}€</span>
                </div>
              </div>
            </div>
          )}

          {/* Add form */}
          <div className="bg-muted/50 rounded-xl p-4 border">
            <p className="text-xs font-medium text-muted-foreground mb-3">
              {incomes.length === 0 ? '➕ Ajouter un revenu' : '➕ Ajouter un autre revenu'}
            </p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <Label className="text-xs">Nom du revenu</Label>
                <Input
                  placeholder="Ex : Salaire, prime, freelance..."
                  value={newIncome.name}
                  onChange={(e) => setNewIncome(prev => ({ ...prev, name: e.target.value }))}
                  className="h-9"
                  data-testid="income-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Montant (€)</Label>
                <Input
                  type="number"
                  placeholder="Ex : 1 800"
                  value={newIncome.amount}
                  onChange={(e) => setNewIncome(prev => ({ ...prev, amount: e.target.value }))}
                  className="h-9"
                  data-testid="income-amount-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <Label className="text-xs">Jour</Label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  value={newIncome.day}
                  onChange={(e) => setNewIncome(prev => ({ ...prev, day: e.target.value }))}
                  className="h-9"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Fréquence</Label>
                <Select
                  value={newIncome.frequency}
                  onValueChange={(v) => setNewIncome(prev => ({ ...prev, frequency: v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ONBOARDING_FREQUENCIES.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              onClick={addIncome}
              disabled={!newIncome.name || !newIncome.amount}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-income-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter ce revenu
            </Button>
          </div>

          {incomes.length === 0 && (
            <p className="text-center text-sm text-muted-foreground">
              Tu n'as pas encore ajouté de revenu
            </p>
          )}
        </div>
      );
    }

    // Expenses step
    if (step === expensesIndex) {
      return (
        <div className="space-y-4">
          <div className="text-center mb-2">
            <TrendingDown className="h-10 w-10 text-rose-500 mx-auto mb-2" />
            <h2 className="text-xl font-bold text-foreground">Tes dépenses fixes 🏠</h2>
            <p className="text-sm text-muted-foreground">Pas de panique, on calcule tout pour toi !</p>
          </div>

          {/* Suggestions */}
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-2">Suggestions rapides :</p>
            <div className="flex flex-wrap gap-2">
              {EXPENSE_SUGGESTIONS.slice(0, 8).map((sug, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => quickAddExpense(sug)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all flex items-center gap-1 ${
                    newExpense.name === sug.name 
                      ? 'bg-rose-200 text-rose-800 border-rose-400' 
                      : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                  }`}
                >
                  <sug.icon className="h-3 w-3" />
                  {sug.name}
                </button>
              ))}
            </div>
          </div>

          {/* List of added expenses */}
          {expenses.length > 0 && (
            <div className="bg-rose-50/50 rounded-xl p-3 border border-rose-200 mb-4">
              <p className="text-xs font-medium text-rose-800 mb-2">
                ✅ Dépenses ajoutées ({expenses.length})
              </p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {expenses.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-rose-200">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm truncate block">{expense.name}</span>
                      <span className="text-xs text-muted-foreground">
                        le {expense.day} • {getFrequencyLabel(expense.frequency, 'mois', 1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className="font-bold text-rose-600 whitespace-nowrap">-{parseFloat(expense.amount).toFixed(0)}€</span>
                      <button 
                        onClick={() => removeExpense(expense.id)} 
                        className="text-gray-400 hover:text-red-500 p-1"
                        data-testid={`remove-expense-${expense.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-rose-200">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Total mensuel :</span>
                  <span className="font-bold text-rose-600">-{totalExpenses.toFixed(0)}€</span>
                </div>
              </div>
            </div>
          )}

          {/* Add form */}
          <div className="bg-muted/50 rounded-xl p-4 border">
            <p className="text-xs font-medium text-muted-foreground mb-3">
              {expenses.length === 0 ? '➕ Ajouter une dépense' : '➕ Ajouter une autre dépense'}
            </p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <Label className="text-xs">Nom de la dépense</Label>
                <Input
                  placeholder="Ex : Loyer, électricité, internet..."
                  value={newExpense.name}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, name: e.target.value }))}
                  className="h-9"
                  data-testid="expense-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Montant (€)</Label>
                <Input
                  type="number"
                  placeholder="Ex : 850"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                  className="h-9"
                  data-testid="expense-amount-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <Label className="text-xs">Jour</Label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  value={newExpense.day}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, day: e.target.value }))}
                  className="h-9"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Fréquence</Label>
                <Select
                  value={newExpense.frequency}
                  onValueChange={(v) => setNewExpense(prev => ({ ...prev, frequency: v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ONBOARDING_FREQUENCIES.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              onClick={addExpense}
              disabled={!newExpense.name || !newExpense.amount}
              className="w-full bg-rose-600 hover:bg-rose-700"
              data-testid="add-expense-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter cette dépense
            </Button>
          </div>

          {expenses.length === 0 && (
            <p className="text-center text-sm text-muted-foreground">
              Tu n'as pas encore ajouté de dépense
            </p>
          )}
        </div>
      );
    }

    // Summary step - REFONTE COMPLÈTE pour clarté Capital vs Flux
    if (step === summaryIndex) {
      const initialBalanceNum = parseFloat(initialBalance) || 0;
      
      return (
        <div className="space-y-4">
          <div className="text-center mb-2">
            <Check className="h-10 w-10 text-teal-500 mx-auto mb-2" />
            <h2 className="text-xl font-bold text-foreground">Récapitulatif</h2>
            <p className="text-sm text-muted-foreground">Vérifie avant de valider</p>
          </div>

          {/* Profile summary (for first time users) */}
          {isFirstTimeUser && (
            <div className="bg-teal-50 rounded-xl p-3 border border-teal-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {profileData.first_name.charAt(0)}{profileData.last_name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-teal-900 text-sm">{profileData.first_name} {profileData.last_name}</p>
                  <p className="text-xs text-teal-700">
                    🎂 {profileData.birthdate ? new Date(profileData.birthdate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : 'Non renseigné'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 1: TON CAPITAL (Solde initial) */}
          {/* ============================================ */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border-2 border-indigo-200">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="h-5 w-5 text-indigo-600" />
              <span className="text-sm font-bold text-indigo-800 uppercase tracking-wide">Ton Capital</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-indigo-600">Ce que tu as aujourd'hui</span>
              <span className="text-2xl font-bold text-indigo-700">{initialBalanceNum.toLocaleString('fr-FR')}€</span>
            </div>
            <p className="text-xs text-indigo-500 mt-1">
              Point de départ au {new Date(initialBalanceDate).toLocaleDateString('fr-FR')}
            </p>
          </div>

          {/* ============================================ */}
          {/* SECTION 2: TON FLUX MENSUEL */}
          {/* ============================================ */}
          <div className="bg-white rounded-xl p-4 border-2 border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <Coins className="h-5 w-5 text-slate-600" />
              <span className="text-sm font-bold text-slate-800 uppercase tracking-wide">Ton Flux Mensuel</span>
            </div>
            
            {/* Revenus et Dépenses */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-emerald-50 rounded-lg p-2.5 border border-emerald-200">
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-800">Revenus</span>
                </div>
                <p className="text-lg font-bold text-emerald-600">+{totalIncome.toLocaleString('fr-FR')}€</p>
                <p className="text-xs text-emerald-600/70">{incomes.length} source{incomes.length > 1 ? 's' : ''}</p>
              </div>
              
              <div className="bg-rose-50 rounded-lg p-2.5 border border-rose-200">
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingDown className="h-3.5 w-3.5 text-rose-600" />
                  <span className="text-xs font-medium text-rose-800">Dépenses</span>
                </div>
                <p className="text-lg font-bold text-rose-600">-{totalExpenses.toLocaleString('fr-FR')}€</p>
                <p className="text-xs text-rose-600/70">{expenses.length} charge{expenses.length > 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Calcul explicite du reste à vivre */}
            <div className={`rounded-lg p-3 border-2 ${monthlyBalance >= 0 ? 'bg-teal-50 border-teal-300' : 'bg-orange-50 border-orange-300'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Revenus − Dépenses =</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Reste à vivre</span>
                <span className={`text-xl font-bold ${monthlyBalance >= 0 ? 'text-teal-600' : 'text-orange-600'}`}>
                  {monthlyBalance >= 0 ? '+' : ''}{monthlyBalance.toLocaleString('fr-FR')}€<span className="text-sm font-normal">/mois</span>
                </span>
              </div>
            </div>
          </div>

          {/* ============================================ */}
          {/* SECTION 3: PROJECTION - Comment ton solde évolue */}
          {/* ============================================ */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border-2 border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-bold text-blue-800 uppercase tracking-wide">Projection</span>
            </div>
            
            {/* Explication visuelle de la connexion */}
            <div className="bg-white/60 rounded-lg p-3 mb-3 border border-blue-200">
              <p className="text-xs text-blue-700 font-medium mb-2">Comment ton solde évolue :</p>
              <div className="flex items-center justify-center gap-2 text-sm">
                <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded font-semibold">{initialBalanceNum.toLocaleString('fr-FR')}€</span>
                <span className="text-slate-400">+</span>
                <span className={`px-2 py-1 rounded font-semibold ${monthlyBalance >= 0 ? 'bg-teal-100 text-teal-700' : 'bg-orange-100 text-orange-700'}`}>
                  {monthlyBalance >= 0 ? '+' : ''}{monthlyBalance.toLocaleString('fr-FR')}€/mois
                </span>
              </div>
            </div>

            {/* Mini graphique de projection sur 6 mois */}
            <div className="space-y-1.5">
              <p className="text-xs text-blue-600 font-medium">Évolution sur 6 mois :</p>
              <div className="grid grid-cols-6 gap-1">
                {projection6Months.map((proj, idx) => (
                  <div key={idx} className="text-center">
                    <div 
                      className={`rounded-md py-1.5 px-1 text-xs font-bold ${
                        proj.balance >= initialBalanceNum 
                          ? 'bg-teal-100 text-teal-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}
                    >
                      {proj.balance >= 10000 
                        ? `${(proj.balance / 1000).toFixed(0)}k` 
                        : proj.balance.toLocaleString('fr-FR')}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{proj.month}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Résultat final */}
            <div className="mt-3 pt-3 border-t border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-800">Si ce rythme reste stable :</span>
                <span className={`text-lg font-bold ${projection6Months[5]?.balance >= initialBalanceNum ? 'text-teal-600' : 'text-orange-600'}`}>
                  {projection6Months[5]?.balance.toLocaleString('fr-FR')}€
                </span>
              </div>
              <p className="text-xs text-blue-500 mt-1 italic">
                Évolution projetée sur 6 mois (estimation basée sur ton flux actuel)
              </p>
            </div>
          </div>

          {/* ============================================ */}
          {/* SECTION 4: CAPACITÉ D'ÉPARGNE (si positive) */}
          {/* ============================================ */}
          {monthlyBalance > 0 && (
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <PiggyBank className="h-4 w-4 text-amber-600" />
                <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">Ta capacité d'épargne</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-700">Reste à vivre</span>
                  <span className="font-medium">{monthlyBalance.toLocaleString('fr-FR')}€</span>
                </div>
                
                {/* Réserve de sécurité - MODIFIABLE */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-700 flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    Réserve sécurité
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-amber-600">-</span>
                    <input
                      type="number"
                      value={customEmergencyReserve !== null ? customEmergencyReserve : defaultEmergencyReserve.toFixed(0)}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          setCustomEmergencyReserve(null);
                        } else {
                          const num = parseFloat(val);
                          // Limiter entre 0 et le reste à vivre
                          setCustomEmergencyReserve(Math.max(0, Math.min(num, monthlyBalance)));
                        }
                      }}
                      className="w-16 h-6 px-1 text-right text-sm font-medium bg-white border border-amber-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-400"
                      min="0"
                      max={monthlyBalance}
                      data-testid="emergency-reserve-input"
                    />
                    <span className="text-amber-700 text-xs">€</span>
                  </div>
                </div>
                {customEmergencyReserve === null && (
                  <p className="text-xs text-amber-500 italic">
                    (Suggestion : 10% des revenus, modifiable)
                  </p>
                )}
                
                <div className="border-t border-amber-300 pt-1.5 flex items-center justify-between">
                  <span className="text-sm font-semibold text-amber-800">Disponible libre</span>
                  <span className="text-lg font-bold text-amber-700">{availableBalance.toFixed(0)}€<span className="text-xs font-normal">/mois</span></span>
                </div>
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 5: Ce qui sera créé */}
          {/* ============================================ */}
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-3 border border-teal-200">
            <p className="text-sm text-teal-800 font-medium mb-2">🚀 En validant, je crée :</p>
            <ul className="text-xs text-teal-700 space-y-0.5">
              {isFirstTimeUser && <li>• Ton profil personnalisé</li>}
              <li>• Ton calendrier avec solde de départ : {initialBalanceNum.toLocaleString('fr-FR')}€</li>
              <li>• {incomes.length} revenu{incomes.length > 1 ? 's' : ''} récurrent{incomes.length > 1 ? 's' : ''} + {expenses.length} dépense{expenses.length > 1 ? 's' : ''} fixe{expenses.length > 1 ? 's' : ''}</li>
              <li>• Projections automatiques mois par mois</li>
            </ul>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md mx-4 bg-background rounded-3xl shadow-2xl border overflow-hidden"
      >
        {/* Progress bar */}
        <div className="px-6 pt-6">
          <div className="flex gap-1.5">
            {steps.map((s, idx) => (
              <div
                key={idx}
                className={`flex-1 h-1.5 rounded-full transition-all ${
                  idx <= step ? 'bg-gradient-to-r from-teal-500 to-emerald-600' : 'bg-muted'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Étape {step + 1} sur {steps.length}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="px-6 pb-6 flex gap-3">
          {step > 0 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-12 rounded-xl"
              data-testid="setup-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
          )}
          
          <Button
            onClick={() => {
              if (step === steps.length - 1) {
                handleSubmit();
              } else {
                // Validation before proceeding
                if (!canProceed()) {
                  if (isFirstTimeUser && step === 1) {
                    if (!profileData.first_name.trim()) {
                      toast.error('Merci de renseigner ton prénom');
                    } else if (!profileData.last_name.trim()) {
                      toast.error('Merci de renseigner ton nom');
                    } else if (!profileData.birthdate) {
                      toast.error('Merci de renseigner ta date de naissance');
                    }
                  } else {
                    toast.error('Merci de remplir les champs requis');
                  }
                  return;
                }
                setStep(step + 1);
              }
            }}
            disabled={loading}
            className="flex-1 h-12 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 rounded-xl"
            data-testid="setup-next-btn"
          >
            {loading ? (
              'Configuration...'
            ) : step === steps.length - 1 ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                {isFirstTimeUser ? "C'est parti ! 🚀" : "Créer ma base financière"}
              </>
            ) : (
              <>
                Continuer
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default FinancialSetupWizard;
